import { Button, buttonVariants, toast } from "@devhop/ui";
import { Link, useParams } from "@tanstack/react-router";
import { ExternalLink, Square, Video } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  useEndLive,
  useLiveDetailQuery,
  useSyncLiveStatus,
} from "@/modules/cf-live/presentation/api/queries";
import { LiveStatusBadge } from "@/modules/cf-live/presentation/ui/LiveStatusBadge";
import { QueryState } from "@/shared/ui/QueryState";
import { useWorkspaceEnabled } from "../api/workspace-client";
import { LiveWorkspacePage } from "./LiveWorkspacePage";

export function LiveDetailPage() {
  const { id } = useParams({ from: "/app/live/$id" });
  const capability = useWorkspaceEnabled();
  if (capability.isLoading) return <p className="p-6">ກຳລັງໂຫຼດ…</p>;
  if (capability.data?.enabled)
    return <LiveWorkspacePage kind="feed" id={id} />;
  return <LegacyLiveDetailPage />;
}

function LegacyLiveDetailPage() {
  const { id } = useParams({ from: "/app/live/$id" });
  const detailQuery = useLiveDetailQuery(id);
  const syncStatus = useSyncLiveStatus(id);
  const endLive = useEndLive(id);

  const detail = detailQuery.data;

  const handleEndLive = async () => {
    if (!window.confirm("ຢືນຢັນຈົບ Live?")) return;
    await endLive.mutateAsync();
  };

  const handleCopyPermalink = async () => {
    const url = detail?.feed.permalinkUrl;
    if (!url) {
      toast.error("ຍັງບໍ່ມີລິງກ໌ Facebook");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("ຄັດລອກລິງກ໌ແລ້ວ");
    } catch {
      toast.error("ຄັດລອກບໍ່ສຳເລັດ");
    }
  };

  return (
    <>
      <Header />
      <Main>
        <QueryState
          isLoading={detailQuery.isLoading}
          isError={detailQuery.isError}
          error={detailQuery.error}
        >
          {detail ? (
            <>
              <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="mb-2 inline-flex items-center gap-2">
                    <Video className="size-5 text-primary" aria-hidden />
                    <h1 className="font-semibold text-2xl tracking-tight">
                      {detail.feed.title ?? "Live"}
                    </h1>
                    <LiveStatusBadge label={detail.displayStatus} />
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {detail.feed.pageName}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void syncStatus.mutateAsync()}
                    disabled={syncStatus.isPending}
                  >
                    ອັບເດດສະຖານະ
                  </Button>
                  {detail.feed.permalinkUrl ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void handleCopyPermalink()}
                    >
                      <span className="inline-flex items-center gap-2">
                        <ExternalLink className="size-4" aria-hidden />
                        ລິງກ໌ Facebook
                      </span>
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => void handleEndLive()}
                    disabled={endLive.isPending}
                  >
                    <span className="inline-flex items-center gap-2">
                      <Square className="size-4" aria-hidden />
                      ຈົບ Live
                    </span>
                  </Button>
                </div>
              </div>

              <div className="mb-6 grid gap-4 sm:grid-cols-3">
                <div className="rounded-lg border p-4">
                  <p className="text-muted-foreground text-xs">ຜູ້ຊົມ</p>
                  <p className="font-semibold text-lg">
                    {detail.liveViews ?? "-"}
                  </p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-muted-foreground text-xs">Graph status</p>
                  <p className="font-semibold text-lg">
                    {detail.graphStatus ?? "-"}
                  </p>
                </div>
                <div className="rounded-lg border p-4">
                  <p className="text-muted-foreground text-xs">Session</p>
                  <p className="font-semibold text-lg">
                    {detail.feed.sessionStatus ?? "-"}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Link
                  to="/app/feeds/$id"
                  params={{ id: detail.feed.id }}
                  search={{ tab: "products" }}
                  className={buttonVariants({ variant: "outline" })}
                >
                  ສິນຄ້າ / ໂໝດ CF
                </Link>
                <Link
                  to="/app/feeds/$id"
                  params={{ id: detail.feed.id }}
                  search={{ tab: "sales" }}
                  className={buttonVariants()}
                >
                  ສະຫຼຸບຍອດຂາຍ
                </Link>
                <Link
                  to="/app/feeds/$id"
                  params={{ id: detail.feed.id }}
                  search={{ tab: "comments" }}
                  className={buttonVariants({ variant: "outline" })}
                >
                  ຄອມເມັນ / ອໍເດີ
                </Link>
              </div>

              <p className="mt-4 text-muted-foreground text-sm">
                <Link to="/app/live" className="underline">
                  ກັບລາຍການ Live
                </Link>
              </p>
            </>
          ) : null}
        </QueryState>
      </Main>
    </>
  );
}
